import axiosClient from "../../../../axios/axios";

function getAuthHeaders(token) {
  return {
    Accept: "application/json",
    "Content-Type": "application/json",
    "x-auth-token": token,
  };
}

function normalizeMeta(responseData) {
  if (!responseData || typeof responseData !== "object") {
    return { payload: {}, updatedAt: null };
  }

  const meta = responseData.meta && typeof responseData.meta === "object" ? responseData.meta : {};

  if (responseData.data && typeof responseData.data === "object") {
    return {
      payload: responseData.data,
      updatedAt:
        meta.savedAt ||
        meta.updatedAt ||
        responseData.savedAt ||
        responseData.updatedAt ||
        responseData.lastSaved ||
        null,
    };
  }

  if (responseData.translations && typeof responseData.translations === "object") {
    return {
      payload: responseData.translations,
      updatedAt: meta.updatedAt || responseData.updatedAt || responseData.lastSaved || null,
    };
  }

  const { updatedAt, lastSaved, savedAt, status, message, meta: _meta, ...rest } = responseData;
  const metaKeys = ["status", "updatedAt", "lastSaved", "savedAt", "message", "success"];
  const hasMeta = metaKeys.some((key) => responseData[key] !== undefined);
  const restKeys = Object.keys(rest);

  if (rest.home) {
    return {
      payload: rest,
      updatedAt: updatedAt || lastSaved || null,
    };
  }

  if (hasMeta && restKeys.length > 0) {
    return {
      payload: rest,
      updatedAt: updatedAt || lastSaved || null,
    };
  }

  return {
    payload: responseData,
    updatedAt: updatedAt || lastSaved || null,
  };
}

async function request(token, { method = "GET", url, body, query }) {
  const headers = getAuthHeaders(token);
  let fullUrl = `/admin${url}`;

  if (query && Object.keys(query).length) {
    const qs = Object.keys(query)
      .filter((key) => query[key] !== undefined && query[key] !== null)
      .map((key) => `${encodeURIComponent(key)}=${encodeURIComponent(query[key])}`)
      .join("&");
    fullUrl = `${fullUrl}?${qs}`;
  }

  let response;
  switch (method) {
    case "GET":
      response = await axiosClient.get(fullUrl, { headers });
      break;
    case "PUT":
      response = await axiosClient.put(fullUrl, body, { headers });
      break;
    case "POST":
      response = await axiosClient.post(fullUrl, body, { headers });
      break;
    default:
      throw new Error(`Unsupported method: ${method}`);
  }

  return response;
}

export async function fetchTranslations(token, languageId) {
  const response = await request(token, {
    method: "GET",
    url: "/translations",
    query: { languageId },
  });
  const normalized = normalizeMeta(response.data);
  return {
    values: normalized.payload,
    updatedAt: normalized.updatedAt,
  };
}

/** Saves translations — live on the website immediately (single save, no draft). */
export async function saveTranslations(token, languageId, values) {
  const response = await request(token, {
    method: "PUT",
    url: "/translations",
    query: { languageId },
    body: values,
  });
  const normalized = normalizeMeta(response.data);
  return {
    values: normalized.payload,
    updatedAt: normalized.updatedAt,
  };
}

export async function fetchPageContent(token, languageId) {
  const response = await request(token, {
    method: "GET",
    url: "/cms/home",
    query: { languageId },
  });
  const normalized = normalizeMeta(response.data);
  return {
    content: normalized.payload,
    updatedAt: normalized.updatedAt,
  };
}

export async function fetchHomeContent(token, languageId) {
  const { content, updatedAt } = await fetchPageContent(token, languageId);
  return {
    home: content.home || content,
    updatedAt,
  };
}

/** Saves a page section — merges with existing CMS document; live immediately. */
export async function savePageSection(token, languageId, section) {
  const response = await request(token, {
    method: "PUT",
    url: "/cms/home",
    query: { languageId },
    body: section,
  });
  const normalized = normalizeMeta(response.data);
  return {
    content: normalized.payload,
    updatedAt: normalized.updatedAt,
  };
}

/** Saves home page — live on the website immediately (single save, no draft). */
export async function saveHomeContent(token, languageId, home) {
  const result = await savePageSection(token, languageId, { home });
  return {
    home: result.content.home,
    updatedAt: result.updatedAt,
  };
}

export async function saveAboutContent(token, languageId, about) {
  const result = await savePageSection(token, languageId, { about });
  return { about: result.content.about, updatedAt: result.updatedAt };
}

export async function saveSolutionsContent(token, languageId, solutions) {
  const result = await savePageSection(token, languageId, { solutions });
  return { solutions: result.content.solutions, updatedAt: result.updatedAt };
}

export async function saveContactContent(token, languageId, contact) {
  const result = await savePageSection(token, languageId, { contact });
  return { contact: result.content.contact, updatedAt: result.updatedAt };
}

export async function uploadMedia(token, file) {
  const formData = new FormData();
  formData.append("file", file);

  const response = await axiosClient.post("/admin/media/upload", formData, {
    headers: {
      Accept: "application/json",
      "x-auth-token": token,
    },
  });

  const data = response.data || {};
  if (data.publicUrl) return data.publicUrl;
  if (data.data && data.data.publicUrl) return data.data.publicUrl;
  if (data.url) return data.url;
  return null;
}
